import { useEffect, useState, useCallback } from "react";
import AdminShell from "../components/AdminShell";
import { api } from "../lib/api";
import { Loader2, RefreshCw, Gift, BadgeDollarSign } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTS = ["requested", "confirmed", "completed", "cancelled"];
const STATUS_CLS = {
  pending_payment: "bg-amber-100 text-amber-800",
  requested: "bg-sky-100 text-sky-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  completed: "bg-neutral-200 text-neutral-700",
  cancelled: "bg-red-100 text-red-800",
};

export default function AdminExpertSessions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/expert-sessions");
      setRows(data.sessions || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (bookingId, status) => {
    try {
      await api.put(`/admin/expert-sessions/${bookingId}/status`, { status });
      setRows((r) => r.map((x) => x.booking_id === bookingId ? { ...x, status } : x));
      toast.success(`Booking ${status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      load(); // re-sync — the select is uncontrolled relative to the server now
    }
  };

  return (
    <AdminShell current="/admin/expert-sessions">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Expert sessions</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Founder bookings for 1-on-1 fundraising sessions. First session free (30 mins);
            follow-ups 60 mins at KES 7,500 (paid via Paystack before they appear as &quot;requested&quot;).
          </p>
        </div>
        <button onClick={load} data-testid="admin-expert-refresh"
          className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:border-neutral-400">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {loading ? (
        <p className="inline-flex items-center gap-2 text-sm text-neutral-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading bookings…
        </p>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-neutral-200 bg-white p-10 text-center text-sm text-neutral-500" data-testid="admin-expert-empty">
          No expert session bookings yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-neutral-200 bg-white" data-testid="admin-expert-table">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-[11px] font-bold uppercase tracking-wider text-neutral-500">
                <th className="px-4 py-3">Booked</th>
                <th className="px-4 py-3">Founder</th>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">CRS</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Preferred slot</th>
                <th className="px-4 py-3">Topic</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.booking_id} className="border-b border-neutral-100 align-top hover:bg-neutral-50/60"
                  data-testid={`admin-expert-row-${r.booking_id}`}>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-neutral-500">
                    {(r.created_at || "").slice(0, 10)}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-neutral-900">{r.name || "—"}</p>
                    <a href={`mailto:${r.email}`} className="text-xs text-[#0A8B85] hover:underline">{r.email}</a>
                  </td>
                  <td className="px-4 py-3 text-neutral-700">{r.company_name || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3">
                    {r.readiness_score != null ? (
                      <span className="font-bold text-neutral-900">{r.readiness_score}<span className="text-xs font-normal text-neutral-400">/100</span></span>
                    ) : <span className="text-xs text-neutral-400">—</span>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    {r.session_type === "free_30" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800">
                        <Gift className="h-3 w-3" /> Free · 30m
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800">
                        <BadgeDollarSign className="h-3 w-3" /> KES {(r.price_kes ?? 7500).toLocaleString()} · 60m
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-neutral-700">
                    {r.preferred_date} <span className="text-neutral-400">at</span> {r.preferred_time}
                  </td>
                  <td className="max-w-[240px] px-4 py-3 text-xs leading-relaxed text-neutral-600">
                    {r.topic || "—"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    {r.status === "pending_payment" ? (
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS_CLS.pending_payment}`}>
                        Payment pending
                      </span>
                    ) : (
                      <select value={r.status}
                        data-testid={`admin-expert-status-${r.booking_id}`}
                        onChange={(e) => setStatus(r.booking_id, e.target.value)}
                        className={`rounded-lg border border-neutral-200 px-2 py-1 text-xs font-semibold ${STATUS_CLS[r.status] || ""}`}>
                        {STATUS_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  );
}
